/**
 * Firma de peticiones del realm staff en el cliente (T017; contracts/staff-api.md, research R-2/R-3,
 * docs/vpp-protocol.md §8.1 y §8.3). Espejo exacto de `apps/api/src/lib/device-signature.ts`; el
 * vector compartido vive en `packages/contracts/tests/fixtures/device-canon.json`.
 *
 *   X-Fermin-Device: v=1; [id=dev_…;] ts=<unix_ms>; nonce=<base64url 12 B>; sig=<base64url ieee-p1363>
 *   canon = METHOD_MAYÚSCULAS \n PATH_sin_query_ni_fragmento \n ts \n nonce \n hex(sha256(body || ""))
 *
 * `id` se omite en `enroll` y `claim` (`includeId: false`): el servidor deriva el `device_id` de la
 * clave pública. Solo WebCrypto y APIs del navegador: nada de `Buffer` ni `node:crypto`.
 */
import { ID_PREFIXES, type PublicKeyJwk } from '@fermin/contracts';

import { getDeviceId, getOrCreateKeyPair } from './keys';

/** Algoritmo de generación/importación de la clave del dispositivo. */
export const DEVICE_KEY_ALGORITHM = { name: 'ECDSA', namedCurve: 'P-256' } as const;
/** Algoritmo de firma: ECDSA sobre SHA-256 (WebCrypto devuelve `r‖s`, ieee-p1363). */
export const DEVICE_SIGN_ALGORITHM = { name: 'ECDSA', hash: 'SHA-256' } as const;

export const DEVICE_HEADER_VERSION = '1';
/** Bytes del nonce (96 bits) → 16 caracteres base64url sin padding. */
export const NONCE_BYTES = 12;
/** `hex(sha256(""))`: hash del cuerpo vacío. */
export const EMPTY_BODY_SHA256_HEX =
  'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855';

// ---------------------------------------------------------------------------
// Codificación
// ---------------------------------------------------------------------------

/** base64url sin padding (RFC 4648 §5). */
export function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Inversa de `base64url`. */
export function base64urlDecode(value: string): Uint8Array {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function hex(bytes: Uint8Array): string {
  let out = '';
  for (const byte of bytes) {
    out += byte.toString(16).padStart(2, '0');
  }
  return out;
}

export function hexToBytes(value: string): Uint8Array {
  const bytes = new Uint8Array(value.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Number.parseInt(value.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

export async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  // Copia a un ArrayBuffer propio: `BufferSource` no admite vistas sobre `SharedArrayBuffer`.
  return new Uint8Array(await crypto.subtle.digest('SHA-256', new Uint8Array(bytes)));
}

// ---------------------------------------------------------------------------
// Canon
// ---------------------------------------------------------------------------

export interface CanonicalInput {
  method: string;
  /** Ruta absoluta sin origin; la query string y el fragmento se descartan. */
  path: string;
  ts: number;
  nonce: string;
  /** `hex(sha256(body || ""))` en minúsculas; ver `bodySha256Hex`. */
  bodySha256Hex: string;
}

/**
 * `PATH` canónico: sin query string ni fragmento (research R-3). Si por error llega una URL
 * absoluta, se usa solo su `pathname` (el servidor firma la ruta, nunca el origin).
 */
export function canonicalPath(pathOrUrl: string): string {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(pathOrUrl)) {
    return new URL(pathOrUrl).pathname;
  }
  const cut = pathOrUrl.search(/[?#]/);
  return cut === -1 ? pathOrUrl : pathOrUrl.slice(0, cut);
}

/** `hex(sha256(body || ""))` sobre los bytes exactos que viajan (el string ya serializado). */
export async function bodySha256Hex(body: string | Uint8Array | null | undefined): Promise<string> {
  if (body === null || body === undefined || body.length === 0) {
    return EMPTY_BODY_SHA256_HEX;
  }
  const bytes = typeof body === 'string' ? new TextEncoder().encode(body) : body;
  return hex(await sha256(bytes));
}

/** `METHOD\nPATH\nts\nnonce\nbodySha256Hex`, idéntico al del servidor. */
export function canonicalString(input: CanonicalInput): string {
  return [
    input.method.toUpperCase(),
    canonicalPath(input.path),
    String(input.ts),
    input.nonce,
    input.bodySha256Hex.toLowerCase(),
  ].join('\n');
}

/** 12 bytes aleatorios → 16 caracteres base64url. */
export function randomNonce(): string {
  const bytes = new Uint8Array(NONCE_BYTES);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

// ---------------------------------------------------------------------------
// device_id
// ---------------------------------------------------------------------------

/** Alfabeto Crockford base32 (el de ULID): sin I, L, O, U. */
const CROCKFORD32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * Codifica exactamente 16 bytes en 26 caracteres crockford32 estilo ULID (3 bits + 25×5 bits),
 * igual que `crockford32From128Bits` del servidor.
 */
export function crockford32From128Bits(bytes: Uint8Array): string {
  if (bytes.length !== 16) {
    throw new RangeError(`crockford32From128Bits espera 16 bytes, recibió ${bytes.length}`);
  }
  let n = 0n;
  for (const byte of bytes) {
    n = (n << 8n) | BigInt(byte);
  }
  const out: string[] = new Array<string>(26);
  for (let i = 25; i >= 0; i -= 1) {
    out[i] = CROCKFORD32[Number(n & 31n)]!;
    n >>= 5n;
  }
  return out.join('');
}

/** `dev_` + crockford32(trunc128(SHA-256(SPKI DER))) a partir de la clave pública WebCrypto. */
export async function deviceIdFromPublicKey(publicKey: CryptoKey): Promise<string> {
  const spki = new Uint8Array(await crypto.subtle.exportKey('spki', publicKey));
  const digest = await sha256(spki);
  return `${ID_PREFIXES.device}_${crockford32From128Bits(digest.subarray(0, 16))}`;
}

/** `dev_` + crockford32(trunc128(SHA-256(SPKI DER))) a partir del JWK público estricto. */
export async function deviceIdFromJwk(jwk: PublicKeyJwk): Promise<string> {
  const publicKey = await crypto.subtle.importKey(
    'jwk',
    { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y, ext: true },
    DEVICE_KEY_ALGORITHM,
    true,
    ['verify'],
  );
  return deviceIdFromPublicKey(publicKey);
}

// ---------------------------------------------------------------------------
// Firma y cabecera
// ---------------------------------------------------------------------------

export interface DeviceHeaderFields {
  /** Ausente en `enroll` y `claim`. */
  id?: string;
  ts: number;
  nonce: string;
  sig: string;
}

/** Serializa la cabecera en el formato exacto del contrato: `v=1; [id=…;] ts=…; nonce=…; sig=…`. */
export function formatDeviceHeader(header: DeviceHeaderFields): string {
  const parts = [`v=${DEVICE_HEADER_VERSION}`];
  if (header.id !== undefined) {
    parts.push(`id=${header.id}`);
  }
  parts.push(`ts=${header.ts}`, `nonce=${header.nonce}`, `sig=${header.sig}`);
  return parts.join('; ');
}

/** ECDSA P-256 / SHA-256 sobre `canon`; WebCrypto ya devuelve `r‖s` (ieee-p1363, 64 bytes). */
export async function signCanon(privateKey: CryptoKey, canon: string): Promise<string> {
  const signature = await crypto.subtle.sign(
    DEVICE_SIGN_ALGORITHM,
    privateKey,
    new TextEncoder().encode(canon),
  );
  return base64url(new Uint8Array(signature));
}

export interface SignRequestInput {
  method: string;
  /** Ruta sin origin (`/api/v1/staff/me`); la query string no se firma. */
  path: string;
  /** El MISMO string (o bytes) que viaja como cuerpo; `undefined` o vacío → hash del cuerpo vacío. */
  body?: string | Uint8Array | null;
  /** `false` en `enroll` y `claim`. Por defecto `true`. */
  includeId?: boolean;
  /** Solo pruebas: fijan `ts` y `nonce`. */
  now?: number;
  nonce?: string;
}

/**
 * Valor de `X-Fermin-Device` para la petición. Crea el par de claves si aún no existe (la primera
 * firma es la de `enroll`). Nunca registrar el valor devuelto en logs.
 */
export async function signRequest(input: SignRequestInput): Promise<string> {
  const pair = await getOrCreateKeyPair();
  const ts = input.now ?? Date.now();
  const nonce = input.nonce ?? randomNonce();
  const canon = canonicalString({
    method: input.method,
    path: input.path,
    ts,
    nonce,
    bodySha256Hex: await bodySha256Hex(input.body),
  });
  const sig = await signCanon(pair.privateKey, canon);
  const header: DeviceHeaderFields = { ts, nonce, sig };
  if (input.includeId !== false) {
    header.id = await getDeviceId();
  }
  return formatDeviceHeader(header);
}
